import { useCallback, useMemo, useState } from 'react';
import { useDirectMessageUserList } from 'shared-state/presence/hooks';
import { useCurrentAreaId, useCurrentRoomId } from 'shared-state/location/hook';
import { useAreaList, useCurrentOrgBranding } from 'shared-state/directory/hooks';
import { useLocalProfile } from 'shared-state/identity/hooks';
import { useCrossAreaSearchBarInputValue, useIsCrossAreaUsersVisible } from 'shared-state/display-options/hooks';
import { IProfile } from 'shared-state/identity/types';
import { getTextColorForBackground } from 'kits/colorKit';
import { ICrossUserProfile, IDirectMessagingState } from '../dataTypes';

type ExpandedIcons = {
    isThisAreaIconExpanded: boolean;
    isOtherAreaIconExpanded: boolean;
    isDashboardIconExpanded: boolean;
    isOutOfOfficeIconExpanded: boolean;
};

export const useDirectMessagingState: () => IDirectMessagingState = () => {
    const [expandedIcons, setExpandedIcons] = useState<ExpandedIcons>({
        isThisAreaIconExpanded: true,
        isOtherAreaIconExpanded: true,
        isDashboardIconExpanded: true,
        isOutOfOfficeIconExpanded: true
    });
    const [directMessageUserList] = useDirectMessageUserList();
    const [currentAreaId] = useCurrentAreaId();
    const [currentRoomId] = useCurrentRoomId();
    const [areas] = useAreaList();
    const [expandedAreas, setExpandedAreas] = useState<string[]>([]);
    const localProfile: IProfile | undefined = useLocalProfile();
    const [crossAreaSearchBarInputValue] = useCrossAreaSearchBarInputValue();
    const [orgBranding] = useCurrentOrgBranding();
    const [, setIsCrossAreaUsersVisible] = useIsCrossAreaUsersVisible();

    const textColor = useMemo(() => {
        const color = orgBranding?.orgColors?.[0];
        return color ? getTextColorForBackground(color) : '#fff';
    }, [orgBranding]);

    const { usersOutsideOfAnArea, usersInsideOfAnArea, usersInCurrentArea, usersInOtherAreas, usersOutOfOffice } = useMemo(() => {
        const list = directMessageUserList ?? [];
        const notSelf = (user: ICrossUserProfile) => user.uid !== localProfile?.uid;
        const sameCampus = (user: ICrossUserProfile) => user.campusId === localProfile?.campusId;
        const isNotOutOfOffice = (user: ICrossUserProfile) => user.user_status?.status !== 'out-of-office';

        const insideArea = list.filter((user) => notSelf(user) && sameCampus(user) && user.userInThisArea && isNotOutOfOffice(user));
        const outsideArea = list.filter((user) => notSelf(user) && sameCampus(user) && !user.userInThisArea && isNotOutOfOffice(user));
        const outOfOfficeUsers = list.filter((user) => notSelf(user) && sameCampus(user) && user.user_status?.status === 'out-of-office');

        const inCurrentArea = insideArea.filter((user) => (currentAreaId ? user.userInThisArea === currentAreaId : false));
        const inOtherArea = insideArea.filter((user) => (currentAreaId ? user.userInThisArea !== currentAreaId : false));

        return {
            usersOutsideOfAnArea: outsideArea,
            usersInsideOfAnArea: insideArea,
            usersInCurrentArea: inCurrentArea,
            usersInOtherAreas: inOtherArea,
            usersOutOfOffice: outOfOfficeUsers
        };
    }, [directMessageUserList, localProfile?.uid, localProfile?.campusId, currentAreaId]);

    const normalizedSearch = (crossAreaSearchBarInputValue ?? '').trim().toLocaleLowerCase();

    const filteredUsers = useMemo(() => {
        if (!normalizedSearch) {
            return {
                outsideOfAnArea: [] as ICrossUserProfile[],
                insideOfAnArea: [] as ICrossUserProfile[],
                inCurrentArea: [] as ICrossUserProfile[],
                inOtherAreas: [] as ICrossUserProfile[],
                outOfOfficeUsers: [] as ICrossUserProfile[]
            };
        }

        const match = (user: ICrossUserProfile) => (user.displayName ?? '').toLocaleLowerCase().includes(normalizedSearch);

        return {
            outsideOfAnArea: usersOutsideOfAnArea.filter(match),
            insideOfAnArea: usersInsideOfAnArea.filter(match),
            inCurrentArea: usersInCurrentArea.filter(match),
            inOtherAreas: usersInOtherAreas.filter(match),
            outOfOfficeUsers: usersOutOfOffice.filter(match)
        };
    }, [normalizedSearch, usersOutsideOfAnArea, usersInsideOfAnArea, usersInCurrentArea, usersInOtherAreas, usersOutOfOffice]);

    const handleToggleAreas = useCallback((areaId: string) => {
        setExpandedAreas((prev) => (prev.includes(areaId) ? prev.filter((id) => id !== areaId) : [...prev, areaId]));
    }, []);

    const handleToggle = useCallback((key: keyof ExpandedIcons) => {
        setExpandedIcons((prev) => ({ ...prev, [key]: !prev[key] }));
    }, []);

    const handleToggleThisArea = useCallback(() => handleToggle('isThisAreaIconExpanded'), [handleToggle]);
    const handleToggleOtherArea = useCallback(() => handleToggle('isOtherAreaIconExpanded'), [handleToggle]);
    const handleToggleDashboard = useCallback(() => handleToggle('isDashboardIconExpanded'), [handleToggle]);
    const handleToggleOutOfOffice = useCallback(() => handleToggle('isOutOfOfficeIconExpanded'), [handleToggle]);

    const getIconName = useCallback((key: keyof ExpandedIcons) => (expandedIcons[key] ? 'ChevronDownMed' : 'ChevronRightMed'), [expandedIcons]);

    const iconNameForThisArea = getIconName('isThisAreaIconExpanded');
    const iconNameForOtherArea = getIconName('isOtherAreaIconExpanded');
    const iconNameForDashboard = getIconName('isDashboardIconExpanded');
    const iconNameForOutOfOffice = getIconName('isOutOfOfficeIconExpanded');

    const isNotFilteredArrayEmpty = useCallback((arr?: ICrossUserProfile[]) => (arr?.length ?? 0) > 0, []);

    return {
        expandedIcons,
        handleToggleThisArea,
        handleToggleOtherArea,
        handleToggleDashboard,
        handleToggleOutOfOffice,
        iconNameForThisArea,
        iconNameForOtherArea,
        iconNameForDashboard,
        iconNameForOutOfOffice,
        currentAreaId,
        areas,
        expandedAreas,
        handleToggleAreas,
        filteredUsers,
        isNotFilteredArrayEmpty,
        textColor,
        crossAreaSearchBarInputValue,
        setIsCrossAreaUsersVisible,
        currentRoomId
    };
};
